import { ButtonProtected, SettingsSection, SubscriptionInvoices } from '@components'

import isEmpty from 'lodash/isEmpty'
import React, { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useBackToSchoolCampaign } from '@features/backToSchoolCampaign/hooks/useBackToSchoolCampaign'
import { useZervantSubscriptionDetails } from '@features/zervant-subscription/hooks/useZervantSubscriptionDetails'
import { SubscriptionPlan } from '@modules-deprecated/app/organization/enums/subscriptionPlan'
import { Plan } from '@modules-deprecated/app/organization/types/plan'

import { Scope } from '../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../enums/trackingContext'
import { useOrganizationSubscription } from '../../../../../../hooks'
import { useOrganizationUiSetup } from '../../../../../../hooks/useOrganizationUiSetup'
import { isExpired } from '../../../../../../utils/payment'
import { useIsAnnualReportAvailable } from '../../../../../annualReport/hooks/useIsAnnualReportAvailable'
import { SettingsRoute } from '../../../../enums/settingsRoute'
import { plansPossibleToChange } from '../../plansData'
import { EditCardSubscriptionParams } from '../../types'
import { AddonsListComponent } from '../AddonsListComponent'
import { BillingSummaryComponent } from '../BillingSummaryComponent'
import { PlanDetailsSideActionsComponent } from '../PlanDetailsSideActionsComponent'
import { SectionPlanDetailsComponent } from '../SectionPlanDetailsComponent'
import { SectionPlanUsageComponent } from '../SectionPlanUsageComponent'
import { ZervantFormerSubscriberBox } from './elements/ZervantFormerSubscriberBox'

interface SubscriptionOverviewProps {
  isInUpdatePeriodState: boolean
  isLoading: boolean
  onEditCardSubscription: (event: EditCardSubscriptionParams) => void
  onSubscriptionPeriodToggle: () => void
  subscriptionPlans: Plan[]
}

export const SubscriptionOverview = ({
  isInUpdatePeriodState,
  isLoading,
  onEditCardSubscription,
  onSubscriptionPeriodToggle,
  subscriptionPlans,
}: SubscriptionOverviewProps): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    switchPlan,
    switchPlanPeriod,
    isSwitchingPlan,
    planExpirationDate,
    subscriptionPlan,
    subscriptionPeriod,
    isTrial,
    discount,
    cardAdded,
    cardExpirationDate,
  } = useOrganizationSubscription()
  const isAnnualReportAvailable = useIsAnnualReportAvailable()
  const { isUiSetupShineInvoicingAccounting } = useOrganizationUiSetup()
  const { isInZervantNoPlan } = useZervantSubscriptionDetails()
  const { trackCampaignEntryPointClicked } = useBackToSchoolCampaign()

  const isShineWithFreeZervantPlan = isUiSetupShineInvoicingAccounting && isInZervantNoPlan

  const isPremiumWithAnnualReports = subscriptionPlan === SubscriptionPlan.Premium && isAnnualReportAvailable
  const shouldShowChangePlanButton =
    isTrial || (plansPossibleToChange.includes(subscriptionPlan) && !isPremiumWithAnnualReports)

  const handleNavigateToPlans = useCallback(() => {
    navigate(SettingsRoute.OrganizationSubscriptionPlansSelection)
  }, [navigate])

  const handleClickChangePlan = useCallback(() => {
    trackCampaignEntryPointClicked({
      ctaLabel: t('organization_subscription.choose_plan'),
      entryPoint: 'subscription_settings_cta',
    })
    handleNavigateToPlans()
  }, [handleNavigateToPlans, t, trackCampaignEntryPointClicked])

  const getAvailableAddons = () => {
    const currentPlan = subscriptionPlans?.filter((planItem) => planItem.id === subscriptionPlan)[0]
    // we currently only offer validated_scans
    return currentPlan ? currentPlan.addons.filter((addon) => addon.id === 'validated_scans') : []
  }

  const wrappedOnEditCardSubscription = useCallback(
    (event: EditCardSubscriptionParams) =>
      onEditCardSubscription({ detail: { ...event.detail, forceExternal: isShineWithFreeZervantPlan } }),
    [onEditCardSubscription, isShineWithFreeZervantPlan],
  )

  const handleEditPaymentInformationClick = useCallback(() => {
    if (cardAdded && !isExpired(cardExpirationDate)) {
      wrappedOnEditCardSubscription({ detail: { cardOnly: true } })
    }
  }, [cardAdded, cardExpirationDate, wrappedOnEditCardSubscription])

  return (
    <>
      <SettingsSection>
        <SettingsSection.RowsContainer>
          <SettingsSection.Content
            title={t('organization_subscription.subscription_overview')}
            sideActions={
              shouldShowChangePlanButton && (
                <PlanDetailsSideActionsComponent onClickChangePlan={handleClickChangePlan} />
              )
            }
          >
            <SectionPlanDetailsComponent
              isTrial={isTrial}
              switchPlan={switchPlan}
              switchPlanPeriod={switchPlanPeriod}
              isSwitchingPlan={isSwitchingPlan}
              planExpirationDate={planExpirationDate}
              subscriptionPeriod={subscriptionPeriod}
              subscriptionPlan={subscriptionPlan}
              onSubscriptionPeriodToggle={onSubscriptionPeriodToggle}
              isInUpdatePeriodState={isInUpdatePeriodState}
              hasDiscount={!!discount && !isEmpty(discount)}
            />
            <SectionPlanUsageComponent subscriptionPlan={subscriptionPlan} onClickChangePlan={handleNavigateToPlans} />
            {getAvailableAddons().length > 0 && getAvailableAddons()[0].value > 0 && (
              <AddonsListComponent addons={getAvailableAddons()} />
            )}
          </SettingsSection.Content>
        </SettingsSection.RowsContainer>
      </SettingsSection>
      <SettingsSection>
        <SettingsSection.RowsContainer>
          <SettingsSection.Content
            title={t('organization_subscription.payment_methods')}
            sideActions={
              cardAdded && (
                <ButtonProtected
                  scopes={Scope.OrganizationPaymentWrite}
                  trackingContext={TrackingContext.PaymentInformationEdit}
                  onClick={handleEditPaymentInformationClick}
                  icon="squarePen"
                  variant="outlined"
                >
                  {t('billing_summary.footer.edit_payment')}
                </ButtonProtected>
              )
            }
          >
            <BillingSummaryComponent isLoading={isLoading} onEditCardSubscription={wrappedOnEditCardSubscription} />
          </SettingsSection.Content>
        </SettingsSection.RowsContainer>
      </SettingsSection>
      <SettingsSection>
        <SettingsSection.RowsContainer>
          <SettingsSection.Content title={t('organization_subscription.paid_invoices')} collapsible>
            <SubscriptionInvoices />
            <ZervantFormerSubscriberBox />
          </SettingsSection.Content>
        </SettingsSection.RowsContainer>
      </SettingsSection>
    </>
  )
}
